
import { defineComponent, watch, computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Mutations } from "../../store/enums/StoreEnums";
import KTUserMenu from "./partials/UserMenu.vue";
import { isCurrentSystem } from "../../core/helpers/unils";
import baseUrlConfig from "../../core/config/BaseUrlConfig";
import { ApiAuth, ApiBase } from "../../core/api";
import Swal from "sweetalert2/dist/sweetalert2.js";

interface User {
  id: number;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
  language: string;
  advanced_manage: number;
  wms_default_warehouse: string;
  settlement_default_owner: string;
  user_type: number;
  show_merge_data: number;
  operating_entity: string[];
}

interface TaggingItem {
  label: string;
  value: string;
}

export default defineComponent({
  name: "topbar",
  components: {
    KTUserMenu,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const userInfo = ref<User>();
    const baseUrl = ref();
    const entity = ref("");
    const options = ref<Array<TaggingItem>>([]);

    userInfo.value = store.getters.currentUser;
    baseUrl.value = baseUrlConfig.baseUrl;

    watch(
      () => store.getters.currentUser,
      (newVal) => {
        userInfo.value = newVal;
        entity.value = String(newVal.settlement_default_owner);
      }
    );

    const showEntity = computed(() => {
      return (
        (isCurrentSystem("fulfillment") ||
          isCurrentSystem("settlement") ||
          isCurrentSystem("pim")) &&
        entity.value != "0" &&
        !userInfo.value?.show_merge_data
      );
    });

    const disableEntity = computed(() => {
      return (
        userInfo.value &&
        userInfo.value.operating_entity &&
        userInfo.value.operating_entity.length <= 1
      );
    });

    const getDropdownOptions = async () => {
      const { data } = await ApiBase.getUserOperatingEntity();
      if (data.code == 0) {
        options.value = data.data;
      }
    };

    const handleChange = (val) => {
      Swal.fire({
        title: t("menu.promptText", { entity: getEntityName(val) }),
        icon: "warning",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: t("menu.promptYes"),
        cancelButtonText: t("menu.promptNo"),
        customClass: {
          confirmButton: "btn btn-warning",
          cancelButton: "btn btn-light",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          setEntity(val);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          entity.value = String(userInfo.value?.settlement_default_owner);
        }
      });
    };

    const setEntity = async (owner: string) => {
      const { data } = await ApiAuth.updateUser({
        id: userInfo.value?.id,
        settlement_default_owner: owner,
      });
      if (data.code == 0) {
        store.commit(
          Mutations.SET_USER,
          Object.assign(userInfo.value as any, {
            settlement_default_owner: owner,
          })
        );
        if (route.meta.rederectToHome) {
          router.replace({ name: "dashboard" });
        } else {
          location.reload();
        }
      }
    };

    const getEntityName = (code: string) => {
      let result = options.value.find((item) => item.value === code);
      return result?.label;
    };

    onMounted(() => {
      getDropdownOptions();
      entity.value = String(userInfo.value?.settlement_default_owner);
    });

    return {
      userInfo,
      baseUrl,
      entity,
      options,
      handleChange,
      setEntity,
      t,
      isCurrentSystem,
      showEntity,
      disableEntity,
    };
  },
});
